import React, { createContext, useContext, useState, ReactNode } from 'react';

interface PopupContextType {
  showPopup: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
  hidePopup: () => void;
  popup: {
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    visible: boolean;
  } | null;
}

const PopupContext = createContext<PopupContextType | undefined>(undefined);

export const usePopup = () => {
  const context = useContext(PopupContext);
  if (context === undefined) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return context;
};

interface PopupProviderProps {
  children: ReactNode;
}

export const PopupProvider: React.FC<PopupProviderProps> = ({ children }) => {
  const [popup, setPopup] = useState<{
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    visible: boolean;
  } | null>(null);

  const showPopup = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setPopup({
      message,
      type,
      visible: true,
    });
  };

  const hidePopup = () => {
    setPopup((prev) => prev ? { ...prev, visible: false } : null);
    // Clear popup after animation
    setTimeout(() => {
      setPopup(null);
    }, 300);
  };

  const value: PopupContextType = {
    showPopup,
    hidePopup,
    popup,
  };

  return (
    <PopupContext.Provider value={value}>
      {children}
    </PopupContext.Provider>
  );
};

